//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import CheckCircle from 'assets/check-circle.svg';
import { ReactComponent as ChevronLeft } from 'assets/chevron-left.svg';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import { ReactComponent as InformationCircle } from 'assets/information-circle.svg';
import { ReactComponent as User } from 'assets/person-circle.svg';
import { AxiosError } from 'axios';
import Footer from 'components/Footer';
import LeaveConfirm from 'components/LeaveConfirm';
import PasswordWithEye from 'components/PasswordWithEye';
import { usePortalAuth } from 'contexts/AuthPortalProvider';
import { useAuth } from 'contexts/AuthProvider';
import { useConfig } from 'contexts/ConfigProvider';
import { useModal } from 'contexts/ModalProvider';
import { useToast } from 'contexts/ToastProvider';
import { UpdatePortalUserPasswordCheckDto } from 'dto/update-portal-user-password-check.dto';
import { useCallbackPrompt } from 'hook/useCallbackPrompt';
import { cbtPortalUserUpdate } from 'models/cbt-portal-user-update';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BuildVersionList, getRoleText } from 'utils/helper';
import { getItem } from 'utils/localStorageHelper';
import Validator from 'utils/validator';

export enum PortalRole {
  Root = 'root',
  Admin = 'admin',
}

const PortalProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { defaultUserEmail, buildVersion } = useConfig();
  const { user, logout, getUser, putUserUpdateMe } = usePortalAuth();
  const siteAuth = useAuth();
  const { addToast } = useToast();
  const { addConfirmModal } = useModal();
  const [usernameEditable, setUserNameEditable] = useState<boolean>(false);
  const [passwordEditable, setPasswordEditable] = useState<boolean>(false);
  const [currentUserRole, setCurrentUserRole] = useState<string>(user.role);
  const isPortalProfile = buildVersion === BuildVersionList.portal && getItem('siteId') === null;
  const validator = Validator();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showDialog);

  const {
    register,
    reset,
    watch,
    trigger,
    handleSubmit,
    formState: { errors, isDirty, isSubmitSuccessful },
  } = useForm<cbtPortalUserUpdate>({
    defaultValues: {
      email: user.email,
      currentPassword: '',
      newPassword: '',
      comfirmPassword: '',
      name: user.name,
    },
  });

  useEffect(() => {
    if (isDirty && !isSubmitSuccessful) {
      setShowDialog(true);
    } else {
      setShowDialog(false);
    }
  }, [isDirty, isSubmitSuccessful]);

  const onSubmit = (data: cbtPortalUserUpdate) => {
    const requsetBody: UpdatePortalUserPasswordCheckDto = {} as UpdatePortalUserPasswordCheckDto;
    const requestName = usernameEditable ? data.name : user.name;
    const requestNewPassword = passwordEditable ? data.newPassword : data.currentPassword;
    Object.assign(requsetBody, { name: requestName, currentPassword: data.currentPassword, newPassword: requestNewPassword });

    putUserUpdateMe(requsetBody)
      .then(() => {
        resetForm();
        getUser();
        addToast({
          title: t('toast.updatedSuccessfully'),
          message: '',
          color: 'bg-toast-success-50',
          icon: CheckCircle,
          text: 'text-toast-success-800',
          svg: 'toastIcon-success',
        });
      })
      .catch((error: AxiosError<{ status: number; ErrorCode: string; ErrorDescription: string }>) => {
        let message: string = '';
        if (
          (error.response?.status === 503 && error.response?.data?.ErrorCode === 'CTL10000') ||
          (error.response?.status === 400 && error.response?.data?.ErrorCode === 'CTL10001') ||
          (error.response?.status === 400 && error.response?.data?.ErrorCode === 'CTL10003') ||
          (error.response?.status === 400 && error.response?.data?.ErrorCode === 'CTL10005') ||
          (error.response?.status === 403 && error.response?.data?.ErrorCode === 'CTL20013') ||
          (error.response?.status === 403 && error.response?.data?.ErrorCode === 'CLD20010')
        ) {
          message = error.response?.data?.ErrorDescription;
        }
        addToast({
          title: t('toast.updatedFailed'),
          message,
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
      });
  };
  const removeAccount = () => {
    addConfirmModal({
      open: true,
      id: 'removeAccountModal',
      name: 'portalAccountRemove',
      btnOk: t('modal.btnOk'),
      btnCancel: t('modal.btnCancel'),
      type: 'warning',
      title: t('portalProfile.removeAdminConfirmTitle'),
      message: (
        <div>
          <p>
            {t('portalProfile.removeAdminConfirmMessageDesc')}
            <br />
            <br />
            {t('portalProfile.removeAdminConfirmMessageNote')}
          </p>
        </div>
      ),
      onClickOk: () => logout(),
    });
  };
  const resetForm = () => {
    reset();
    setUserNameEditable(false);
    setPasswordEditable(false);
  };

  useEffect(() => {
    if (getItem('siteId')) {
      setCurrentUserRole(siteAuth.user.role);
    } else {
      setCurrentUserRole(user.role);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getItem('siteId')]);

  useEffect(() => {
    reset({
      email: user.email,
      currentPassword: '',
      newPassword: '',
      comfirmPassword: '',
      name: user.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    Object.keys(errors).length > 0 && trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  return (
    <div className="h-screen flex flex-col bg-gray-50 dark:bg-transparent">
      <main className="flex-1">
        <div className="flex px-5 py-4 gap-1 flex-col h-full">
          {getItem('siteId') ? (
            <></>
          ) : (
            <h2 className="flex item-center gap-2 pl-4 py-2 bg-base-100 shadow font-semibold dark:bg-gray-800 dark:shadow-none">
              <button type="button" onClick={() => navigate('/')} data-testid="portal-profile-back-prev-btn">
                <ChevronLeft className="w-4 h-4 stroke-2" />
              </button>
              {t('portalProfile.pageTitleAccountProfile')}
            </h2>
          )}
          <div className="bg-base-100 shadow mb-6 h-full dark:bg-gray-800 dark:shadow-none">
            <form className={`h-full ${isPortalProfile ? '' : ''}`} onSubmit={handleSubmit(onSubmit)}>
              <div className="px-5">
                <div className="flex justify-between items-center p-8">
                  <div className="flex items-center justify-center">
                    <div className="">
                      <User />
                    </div>
                    <div className="flex flex-col justify-center px-3">
                      <div className="text-sm opacity-50">{getRoleText(currentUserRole)}</div>
                      <div className="font-bold">{user.email}</div>
                    </div>
                  </div>
                  <button type="button" className="btn btn-primary" onClick={logout} data-testid="portal-profile-log-out-btn">
                    {t('modal.btnLogOut')}
                  </button>
                </div>
                <div className="flex items-center justify-start border-t border-gray-350 px-8 py-2">
                  <label className="label-text w-64 py-2">{t('portalProfile.formUserName')}</label>
                  <div className="flex flex-1 flex-col">
                    {usernameEditable ? (
                      <>
                        <input
                          type="text"
                          className={`input max-w-xs ${errors.name ? 'input-error' : ''}`}
                          {...register('name', {
                            value: user.name,
                            required: validator.required,
                            minLength: validator.minLength(1, 32),
                            maxLength: validator.maxLength(1, 32),
                            pattern: validator.namePassword,
                            validate: {
                              defaultUserEamilDuplicate: (v) =>
                                v !== defaultUserEmail ||
                                t('portalProfile.formUserNameErrorDuplicatedWithDefaultEmail', {
                                  defaultUserEmail,
                                }),
                              checkSpace: (v) => !v || validator.checkNameBeginEndWithSpace(v),
                            },
                          })}
                          data-testid="portal-profile-input-for-name-name"
                        />
                        {errors.name && <span className="span-error">{errors.name.message}</span>}
                      </>
                    ) : (
                      user.name
                    )}
                  </div>
                  {!usernameEditable && (
                    <button
                      type="button"
                      className="btn btn-link text-[#489EE5]"
                      onClick={() => setUserNameEditable(true)}
                      data-testid="portal-profile-rename-btn"
                    >
                      {t('modal.btnRename')}
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-start border-y border-gray-350 px-8 py-2">
                  <label className="label-text w-64 flex py-2">
                    <span>{t('portalProfile.formEmail')}</span>
                    <div className="ml-1 tooltip tooltip-right text-left info-tooltip" data-tip={t('portalProfile.formEmailDataTip')}>
                      <InformationCircle className="w-5 h-5 text-cyfi-btn" />
                    </div>
                  </label>
                  <div className="flex flex-1">{user.email}</div>
                </div>
                <div className={`flex items-center justify-start border-gray-350 px-8 py-2 ${passwordEditable ? '' : 'border-b'}`}>
                  <label className="label-text w-64 flex py-2">
                    <span>{t('portalProfile.formCurrentPassword')}</span>
                  </label>
                  <div className="flex flex-1 flex-col">
                    {passwordEditable || usernameEditable ? (
                      <div>
                        <PasswordWithEye
                          register={{
                            ...register('currentPassword', {
                              required: validator.required,
                              minLength: validator.minLength(8, 63),
                              maxLength: validator.maxLength(8, 63),
                              pattern: validator.userPassword,
                            }),
                          }}
                          placeholder={t('portalProfile.formCurrentPasswordPlaceholder')}
                          errorsName={errors.currentPassword}
                        />
                        {errors.currentPassword && <span className="span-error">{errors.currentPassword.message}</span>}
                      </div>
                    ) : (
                      <p>******</p>
                    )}
                  </div>
                  {!passwordEditable && (
                    <button
                      type="button"
                      className="btn btn-link text-[#489EE5]"
                      onClick={() => setPasswordEditable(true)}
                      data-testid="portal-profile-change-pwd-btn"
                    >
                      {t('modal.btnChangePassword')}
                    </button>
                  )}
                </div>
                {/* New Password */}
                {passwordEditable && (
                  <div className="flex justify-start px-8 py-2">
                    <label className="label-text w-64 flex py-2">
                      <span>{t('portalProfile.formNewPasswrod')}</span>
                      <div className="ml-1 tooltip tooltip-right text-left info-tooltip" data-tip={t('portalProfile.formNewPasswrodDataTip')}>
                        <InformationCircle className="w-5 h-5 text-cyfi-btn" />
                      </div>
                    </label>
                    <div className="flex flex-1 flex-col">
                      <div>
                        <PasswordWithEye
                          register={{
                            ...register('newPassword', {
                              required: validator.required,
                              minLength: validator.minLength(8, 63),
                              maxLength: validator.maxLength(8, 63),
                              pattern: validator.userPassword,
                              validate: {
                                currentPasswordDuplicate: (v) => v !== watch('currentPassword') || t('portalProfile.formNewPasswordDuplicateCurrent'),
                                matchWithComfirmPassword: (v) => v === watch('comfirmPassword') || t('portalProfile.formNewPasswordMismatch'),
                              },
                            }),
                          }}
                          placeholder={t('portalProfile.formNewPasswrod')}
                          errorsName={errors.newPassword}
                        />
                        {errors.newPassword && <span className="span-error">{errors.newPassword.message}</span>}
                      </div>
                    </div>
                  </div>
                )}
                {/* Comfirm Password */}
                {passwordEditable && (
                  <div className="flex justify-start px-8 py-2">
                    <label className="label-text w-64"></label>
                    <div className="flex flex-1 flex-col">
                      <div>
                        <PasswordWithEye
                          register={{
                            ...register('comfirmPassword', {
                              required: validator.required,
                              minLength: validator.minLength(8, 63),
                              maxLength: validator.maxLength(8, 63),
                              pattern: validator.userPassword,
                              validate: {
                                matchWithNewPassword: (v) => v === watch('newPassword') || t('portalProfile.formCofirmNewPasswordMismatch'),
                              },
                            }),
                          }}
                          placeholder={t('portalProfile.formCofirmNewPasswordPlaceholder')}
                          errorsName={errors.comfirmPassword}
                        />
                        {errors.comfirmPassword && <span className="span-error">{errors.comfirmPassword.message}</span>}
                      </div>
                    </div>
                  </div>
                )}
                {/*
            <div className="label justify-start border-t border-[#CBD5E1] px-8 text-[#1F2937]">
              <label className="label-text w-64 flex py-2">
                <span>Account and Data</span>
              </label>
              <div className="flex flex-1"></div>
              <div>
                <button type="button" className="btn btn-link text-[#489EE5]" onClick={removeAccount}>
                  Remove from Cloud
                </button>
              </div>
            </div>
            */}
              </div>
              {(isDirty || usernameEditable || passwordEditable) && (
                <div className="flex justify-end space-x-2 mt-5 px-8">
                  <button type="button" className="btn btn-outline" onClick={resetForm} data-testid="cancel-portal-profile-btn">
                    {t('modal.btnCancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" data-testid="apply-portal-profile-btn">
                    {t('modal.btnApply')}
                  </button>
                </div>
              )}
            </form>
            <LeaveConfirm showDialog={showPrompt} confirmNavigation={confirmNavigation} cancelNavigation={cancelNavigation} />
          </div>
        </div>
      </main>
      {isPortalProfile && <Footer />}
    </div>
  );
};

export default PortalProfile;
